import Vue, { Component } from 'vue';
import VueRouter, { Route } from 'vue-router';
import _ from '../../utils';

export default class RouterUtils {
  private router: VueRouter;

  constructor(router: VueRouter) {
    this.router = router;
  }

  getQueryParams() {
    const queries = {} as Record<string, any>;
    const queryStrings = window.location.search.replace('?', '').split('&');
    queryStrings.forEach((queryString) => {
      const queryParts = queryString.split('=');
      if (queryParts.length === 2) {
        const val = queryParts[1] as string;
        queries[queryParts[0]] = decodeURIComponent(val);
      }
    });
    return queries;
  }

  queryParamsToObject(obj: object) {
    const queries = this.getQueryParams();
    Object.keys(queries).forEach((queryName) => {
      _.deepSet(obj, queryName, queries[queryName]);
    });
  }

  updateQueryParams(queries: Record<string, any>, options?: {preserve?: boolean, depth?: number, filterEmptyString?: boolean, filterUndefined?: boolean, filterNull?: boolean}) {
    const filterEmptyString = options?.filterEmptyString ?? true;
    const filterUndefined = options?.filterUndefined ?? true;
    const filterNull = options?.filterNull ?? false;
    const preserve = options?.preserve ?? true;
    const depth = options?.depth;
    const currentPath = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
    const valMap = (val: any) => {
      if (typeof val === 'string') return encodeURIComponent(`${val}`);
      if (val === undefined) return 'undefined';
      if (val === null) return 'null';
      return val;
    };
    let combinedQueries = queries;
    const flatQueries = _.flattenObject(combinedQueries, '.', depth);
    if (preserve) {
      const existingQueries = this.getQueryParams();
      combinedQueries = { ...existingQueries, ...flatQueries };
    }
    const queryString = Object.keys(combinedQueries)
      .filter((qName) => !filterEmptyString || combinedQueries[qName] !== '')
      .filter((qName) => !filterUndefined || combinedQueries[qName] !== undefined)
      .filter((qName) => !filterNull || combinedQueries[qName] !== null)
      .map((qName) => `${qName}=${valMap(combinedQueries[qName])}`)
      .join('&');
    const newPath = `${currentPath}?${queryString}`;
    window.history.replaceState({}, '', newPath);
  }

  /** Returns the first option for the route in a matched component */
  async getRouteOption<T>(route: Route, key: string): Promise<T | undefined> {
    const components = await this.getComponentsForRoute(route) as any[];
    let optionValue: T | undefined;
    const found = components.find((component) => {
      if (component?.options !== undefined) {
        if (component.options[key] !== undefined) {
          optionValue = component.options[key];
          return true;
        }
      } else if (component?.default !== undefined) {
        if (component.default.options !== undefined) {
          if (component.default.options[key] !== undefined) {
            optionValue = component.default.options[key];
            return true;
          }
        }
        if (component?.default[key] !== undefined) {
          optionValue = component.default[key];
          return true;
        }
      } else if (component?.[key] !== undefined) {
        optionValue = component[key];
        return true;
      }
      return false;
    });
    return optionValue;
  }

  /** Returns and resolves matched components from a route */
  async getComponentsForRoute(route: Route) {
    const { matched } = route;
    const components = [] as Component[];
    for (let i = 0; i < matched.length; i += 1) {
      const r = matched[i];
      let component = r.components.default as any;
      if (component?.cid === undefined) {
        if (_.isPromise(component)) {
          // eslint-disable-next-line no-await-in-loop
          component = await component;
        } else if (typeof (component) === 'function') {
          try {
            component = component();
            if (_.isPromise(component)) {
              // eslint-disable-next-line no-await-in-loop
              component = await component;
              if (component?.cid === undefined) {
                component = component?.default;
              }
            }
          } catch (e) {
            Vue.$log.error(e);
          }
        }
      }
      components.push(component);
    }
    return components;
  }

  /**
   * Compares current route with another and normalizes them (removes queries)
   */
  insideRoute(route: string | Route) {
    return this.areRoutesSame(Vue.$router.currentRoute, route);
  }
  /**
   * Compares two routes, ignores query parameters
   */
  areRoutesSame(routeOne: string | Route, routeTwo: string | Route) {
    return this.normalizeRoutePath(routeOne) === this.normalizeRoutePath(routeTwo);
  }

  /**
   * Remove queries, remove
   */
  normalizeRoutePath(route: string | Route) {
    if (route === undefined || route === null) {
      return route;
    }
    let routePath = (route as string);
    if (typeof route !== 'string') {
      routePath = route.fullPath;
    }

    // Remove query string
    let result = routePath.split('?')[0];

    // Remove redundant / from the end of path
    if (result.charAt(result.length - 1) === '/') {
      result = result.slice(0, -1);
    }
    return result;
  }
}
